import { useMemo, useState } from "react"
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux"

import Item from "../Shared/Item"
import Tag from "../../../fragments/Tag"
import DeleteItem from "../Shared/DeleteItem"
import CardGrey from "../../../common/CardGrey"
import { caseActions } from "../../../../store/case"
import { caseQuickUpdate, getMajorMedFromPolicy } from "../../../../lib/fetchApi"
import { notificationActions } from "../../../../store/notifications"
import PrimaryCheckbox from "../Shared/PrimaryCheckbox"
import GeneralVerifiedBy from "../medpay/GeneralVerifiedBy"
import MajorMedSidebar from "./MajorMedSidebar"
import MajorMedCarrierSelect from "./MajorMedCarrierSelect"
import MajorMedPayments from "./MajorMedPayments"
import MajorMedPaymentsControls from "./MajorMedPaymentsControls"
import InsuranceAddress from "../Shared/InsuranceAddress"
import MajorMedDepartments from "./MajorMedDepartments"
import { formatDateLocal, hideSSNString, isDateInThePast } from "../../../../lib/script"
import { formatPatientName } from "../../../../lib/patientName"
import BillingOrder from "./BillingOrder"
import PrimaryInsured from "./PrimaryInsured"
import Modal from "../../../common/Modal"
import ModalInnerWrapper from "../../../common/ModalInnerWrapper"
import PannelBody from "../../../fragments/PannelBody"
import Backdrop from "../../../common/modal/Backdrop";
import Button from "../../../common/Button";
import GroupContainer from "../GroupNew/GroupContainer";
import { disableVerifySelection } from "../../../../lib/verifications";
import { departmentsOptions, frontendOptions, majorMedPolicyStatusOptionsPayerTypes } from "../../../../lib/systemData";
import DefaultUpdater from "../../../common/DefaultUpdater";
import CopyIconButton from "../../../fragments/CopyIconButton"
import {
    copyLineAlways,
    copyLineInnOonAlways,
    getInsuranceLocationContact,
    getLastNoteForType,
    joinCopyLines,
} from "../../../../lib/insuranceCopyNotes"

import classes from '../Shared/Item.module.css'
import PayerTypeStatusGlobalModal from "../../../common/modal/PayerTypeStatusGlobalModal";
import VerificationsHistory from "../verifications/VerificationsHistory";
import IsVerifiedTag from "../verifications/IsVerifiedTag";

const MajorMedItem = ({data, apiCase}) => {

    const dispatch = useDispatch()

    let itemData = data

    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase) 

    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [matchedInformation, setMatchedInformation] = useState(null)

    async function updateAutomaticaly(value) { //if the ins owner is self == 1 then the name, dob, ssn should be copied     
        if (value.data.value == 1) {
            
            let patient = activeCase.patient  
     
            let reqBody = {
                type: apiCase,
                type_id: itemData.id,
                fields: {
                    name_of_primary_insured : formatPatientName(patient),
                    date_of_birth : patient.dob,
                    ssn : patient.ssn,
                }
            }

            let dispatchObj = {
                folder: 'major_meds',
                item: {
                    itemId: itemData.id,
                    fields: [
                        {
                            field: 'name_of_primary_insured',
                            value: formatPatientName(patient)
                        },
                        {
                            field: 'date_of_birth',
                            value: patient.dob
                        },
                        {
                            field: 'ssn',
                            value: patient.ssn
                        },
                    ]            
                }
            }

            setTimeout(() => {
                dispatch(caseActions.modifyActiveCaseMultiple(dispatchObj))
            }, 0)

            await caseQuickUpdate(userToken, activeCase.case.id, reqBody)
        }
    }

    async function policyNumberExtraAction(value) { //if the system find the same policy number show the modal to copy the information!
        if (value.data.value) {
            const match = await getMajorMedFromPolicy(userToken, activeCase.case.id, {
                policy_number: value.data.value
            })

            if (match) { // show a modal
                setMatchedInformation(match)
                setShowModal(true)
            } else {
                setMatchedInformation(null)
            }
        }
    }

    function closeModalHandler() {
        setShowModal(false)
    }

    async function copyInformationHandler() {
        setLoading(true)
        try {
            // remove unwanted information 
            delete matchedInformation.patient
            delete matchedInformation.case
            delete matchedInformation.insurance_carrier
            delete matchedInformation.insurance_carrier_id

            const reqBody = {
                type: apiCase,
                type_id: data.id,
                fields: {
                    ...matchedInformation
                }
            }
            let arr = []
            for (const [key, value] of Object.entries(matchedInformation)) {     
                arr.push({
                    field: key,
                    value: value
                })
            }
           
            const dispatchObj = {
                folder: 'major_meds',
                item: {
                    itemId: data.id,
                    fields: [...arr]              
                },
            }

            const update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)

            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update))
            } else {    
                setTimeout(() => {
                    dispatch(caseActions.modifyActiveCaseMultiple(dispatchObj))
                }, 0)
                setShowModal(false)
            } 
        } finally {
            setLoading(false)
        }
    }

    const majorMedCopyNotesText = useMemo(() => {
        const loc = getInsuranceLocationContact(data, "major_meds", activeCase)
        const departments = departmentsOptions() ?? []
        const chiroDept = departments.find((d) => /chiro/i.test(String(d.name ?? "")))
        const chiroRow =
            chiroDept && Array.isArray(data.major_med_departments)
                ? data.major_med_departments.find((r) => r.department_id == chiroDept.id)
                : null
        const eff = data.effective_date ? formatDateLocal(data.effective_date) : ""
        const lastNote = getLastNoteForType(activeCase?.notes, 7)

        const groupSection = joinCopyLines([
            "GROUP INSURANCE",
            copyLineAlways("Insurance Co", data.insurance_carrier?.name),
            copyLineAlways("Policy", data.policy_number),
            copyLineAlways("Group No", data.group_number),
            copyLineAlways("Out of NW Ben", null),
            copyLineAlways("Effective Date", eff || null),
            copyLineAlways("Address", loc.address1),
            copyLineAlways("City", loc.city),
            copyLineAlways("State", loc.state),
            copyLineAlways("Zip", loc.zip),
        ])

        const medicalSection = joinCopyLines([
            "MEDICAL",
            copyLineInnOonAlways("Deductible", data.deductible_inn, data.deductible_oon),
            copyLineInnOonAlways("Per Year?", data.out_pocket_max_inn, data.out_pocket_max_oon),
            copyLineInnOonAlways("Amt. Satisfied", data.remaining_1_inn, data.remaining_1_oon),
            copyLineAlways("Out Pt. Paid At", data.coinsurance_oon),
            copyLineAlways("IN Pays at", data.coinsurance_inn),
        ])

        const chiroSection = joinCopyLines([
            "OUT OF NETWORK CHIRO",
            copyLineAlways("Limitations?", chiroRow?.limit),
        ])

        return [groupSection, medicalSection, chiroSection, copyLineAlways("Notes", lastNote)].join("\n\n")
    }, [data, activeCase])
    
    return <Item data={data} mainItem={data.is_primary_insurance} type={apiCase} folder={'major_meds'}>
        <div className={classes.item_header}>
            {data.insurance_carrier && <>
                <div>
                    <div className={classes.name}>
                        <CopyIconButton textToCopy={majorMedCopyNotesText} title="Copy Major Med notes" />
                        <Tag type={'note'}>{data.insurance_carrier.code}</Tag>
                        <h2>{data.insurance_carrier.name}</h2>                      
                    </div>
                    {data.insurance_carrier.contact_information && <div className={classes.location}>
                        <i className='ti ti-building-skyscraper'></i>
                        <InsuranceAddress data={data} folder={'major_meds'}/>
                    </div>}
                </div>
                <div className={classes.header_controls}>
                    {/* <IsVerifiedTag isVerified={data.verified}>{data.verified ? 'Verified' : 'Not Verified'}</IsVerifiedTag> */}
                    <div className={classes.row}>                        
                        <BillingOrder itemData={itemData} />   {/* NOT USING THE DEFAULTUPDATER! */}
                    </div>
                    <div style={{width: 300}}>
                        <MajorMedCarrierSelect data={data}/>
                    </div>
                    <DeleteItem data={data} type={apiCase} folder={'major_meds'} label={'insurance'}/>
                </div>
            </>}
        </div>
        <div className={classes.item_inner}>
            <div className={classes.item_left}>
                <div >
                    <div className={classes.row}>
                        <CardGrey>
                            <PrimaryCheckbox data={data} label={'Primary Insurance'} folder={'major_meds'} apiCase={apiCase} propertyName={'is_primary_insurance'}/>
                        </CardGrey>                        
                        <div style={{flex: 2}}>
                            <CardGrey >
                                <DefaultUpdater type={'radio'} value={data.ins_owner} options={frontendOptions()?.majorMedOwnerRadion} name={'ins_owner'} identifier={{type: apiCase, field: 'ins_owner', type_id: data.id, folder: 'major_meds'}} extraOptions={{normalSize: true}} extraAction={updateAutomaticaly}/>
                            </CardGrey>
                        </div >                        
                    </div>

                    <div className={classes.row}>
                        <CardGrey>
                            <PrimaryInsured data={data} />                        
                        </CardGrey>   
                        <CardGrey>
                            <PayerTypeStatusGlobalModal date={data.policy_status_date} user={data.policy_status_by} isDisabled={!data.policy_status}>                                  
                                <DefaultUpdater type={'select'} value={data.policy_status} options={majorMedPolicyStatusOptionsPayerTypes()} label={'Policy Status'} identifier={{type: apiCase, field: 'policy_status', type_id: data.id, folder: 'major_meds'}} extraOptions={{hasRemove: true}}/>
                            </PayerTypeStatusGlobalModal>
                        </CardGrey>                     
                    </div>
                    
                    <div className={classes.row}>
                        <div className={classes.row_item_full}>
                            <CardGrey>
                                <div className="flex-container-equal">
                                    <DefaultUpdater type={'input'} value={data.payer_id} name={'payer_id'} label={'Payer ID'} identifier={{type: apiCase, field: 'payer_id', type_id: data.id, folder: 'major_meds'}} />

                                    <DefaultUpdater type={'input'} value={data.policy_number} name={'policy_number'} label={'Policy Number'} identifier={{type: apiCase, field: 'policy_number', type_id: data.id, folder: 'major_meds'}} extraAction={policyNumberExtraAction}/>
                                    {/* extraOptions={{redBorderFlag: !data.policy_number}} */}

                                    <DefaultUpdater type={'input'} value={data.group_number} name={'group_number'} label={'Group Number'} identifier={{type: apiCase, field: 'group_number', type_id: data.id, folder: 'major_meds'}}/>

                                    <DefaultUpdater type={'date'} value={data.effective_date} label={'Effective Date'} identifier={{type: apiCase, field: 'effective_date', type_id: data.id, folder: 'major_meds'}}/>

                                    <DefaultUpdater type={'date'} value={data.cancel_date} label={'Cancel Date'} identifier={{type: apiCase, field: 'cancel_date', type_id: data.id, folder: 'major_meds'}}/>

                                    <DefaultUpdater type={'date'} value={data.end_date} label={'End Date'} identifier={{type: apiCase, field: 'end_date', type_id: data.id, folder: 'major_meds'}} extraOptions={{setStyle: isDateInThePast(data.end_date) ? 'border_red' : ''}}/>
                                </div>
                            </CardGrey>
                        </div>                        
                    </div>

                    <div className={classes.row}>
                        <div className={classes.row_item_full}>
                            <CardGrey>
                                <VerificationsHistory data={data} itemId={data.id} verificationHistory={data.verification_history} folder={'major_meds'} verificationField={'verification_history'} caseType={apiCase}/>
                            </CardGrey>
                        </div>
                    </div>

                    <div className={classes.row}>
                        <div className={classes.row_item_full}>
                            <CardGrey>
                                {/* <GeneralVerifiedBy data={data} folder={'major_meds'} caseType={apiCase} areFieldsNotModifiable={disableVerifySelection(data, 'major_meds')}/> */}

                                <div className={classes.group_container}>
                                    <GroupContainer data={data} smsMail={'Insurance Carrier'} dataId={data.id} label={'location'} folder={'major_meds'} apiCase={apiCase} fieldsData={{fields: ['location_id', 'location'], fieldId: 'location_id', fieldobj: 'location', group: 'locations', inputLabel: 'Location'}}/>
                                </div>

                                <div className={classes.group_container}>
                                    <GroupContainer data={data} smsMail={'Adjuster'} dataId={data.id} label={'adjuster'} folder={'major_meds'} apiCase={apiCase} fieldsData={{fields: ['adjuster_id', 'adjuster'], fieldId: 'adjuster_id', fieldobj: 'adjuster', group: 'adjusters', inputLabel: 'Adjuster'}}/>
                                </div>
                            </CardGrey>
                        </div>
                    </div>
                                        
                </div>
                
            </div>

            <div className={classes.item_right}>
                <MajorMedSidebar data={data} />
            </div>      
        </div> 

        <div className={classes.item_extra}>
            <MajorMedPayments itemData={data} apiCase={apiCase}/>
            <MajorMedPaymentsControls itemData={data} apiCase={apiCase}/>
            <MajorMedDepartments data={data}/>  {/* uses 'case_major_med_departments' */}
            
        </div>

        {showModal && <>
            {ReactDOM.createPortal(
                <Backdrop hide={closeModalHandler} />,
                document.getElementById("backdrop-root")
            )}
            {ReactDOM.createPortal(
                <Modal>
                    <ModalInnerWrapper closeModal={closeModalHandler}  modalTitle={'Match found'}>
                        <PannelBody>
                            <p>We've  found the same policy already in the system, would you want to copy the information?</p>
                            <p><strong>Primary insured:</strong> {matchedInformation.name_of_primary_insured}, <br />
                                <strong>DOB:</strong> {formatDateLocal(matchedInformation.date_of_birth)}, <br />
                                <strong>SSN:</strong> {hideSSNString(matchedInformation.ssn)}</p>
                            <p><strong>Payer id:</strong> {matchedInformation.payer_id}, <br />
                                <strong>Group Number:</strong> {matchedInformation.group_number}, <br />
                                <strong>Effective Date:</strong> {formatDateLocal(matchedInformation.effective_date)}, <br />
                                <strong>End Date:</strong> {formatDateLocal(matchedInformation.end_date)}</p>
                        </PannelBody>
                        <PannelBody>
                            <div className="flex-container">
                                <Button click={closeModalHandler} btnStyle={'btn_light_grey'}>Cancel</Button>
                                <Button isLoading={loading} click={copyInformationHandler}>Proceed</Button>
                            </div>
                        </PannelBody>
                    </ModalInnerWrapper>    
                </Modal>,
                document.getElementById("overlay-root")
            )}  
            
        </>}
    </Item>
}

export default MajorMedItem